import 'cropperjs/dist/cropper.css'
import './cropper.css'

import { Box, IconButton, Stack, Typography } from '@mui/material'
import { IconTrashBinFilled } from 'assets/icons'
import { DialogContent } from 'elements/feedback'
import { DropZone } from 'elements/inputs'
import { useBoolean } from 'lib/hooks'
import { useState } from 'react'
import { Cropper } from 'react-cropper'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

const SUPPORTED_FILE_FORMAT = 'JPEG, JPG, PNG'

type ImageEditorProps = {
  onClose: () => void
  isLoading: boolean
  onConfirm: (blob: Blob) => void
  dialogTitle: string
}

export const ImageEditor = ({
  onClose,
  isLoading,
  onConfirm,
  dialogTitle,
}: ImageEditorProps) => {
  const { t } = useTranslation()

  const [imageDataUrl, setImageDataUrl] = useState('')

  const isError = useBoolean()

  const [cropper, setCropper] = useState<Cropper | null>(null)

  const isCropping = useBoolean()

  const handleFileDrop = (file: File) => {
    const reader = new FileReader()

    reader.addEventListener('load', () => {
      const result = reader.result
      setImageDataUrl(result as string)
    })

    reader.readAsDataURL(file)
  }

  return (
    <DialogContent
      title={dialogTitle}
      onConfirm={() => {
        if (cropper) {
          isCropping.setTrue()

          cropper.getCroppedCanvas().toBlob(blob => {
            isCropping.setFalse()

            if (blob) {
              onConfirm(blob)
            }
            // So that the size of the exported image is not greater than the original
          }, 'image/jpeg')
        }
      }}
      isConfirmDisabled={!imageDataUrl}
      isActionLoading={isLoading || isCropping.isTrue}
      onDeny={onClose}
    >
      {imageDataUrl ? (
        <Box height="214px" position="relative" borderRadius="8px">
          <Cropper
            src={imageDataUrl}
            viewMode={1}
            dragMode="move"
            aspectRatio={1}
            minCropBoxWidth={32}
            minCropBoxHeight={32}
            checkOrientation={false}
            guides={false}
            center={false}
            zoomable={false}
            movable={false}
            scalable={false}
            rotatable={false}
            background={false}
            onInitialized={setCropper}
            style={{ height: '100%' }}
          />

          <IconButton
            color="error"
            onClick={() => setImageDataUrl('')}
            sx={{ position: 'absolute', right: 14, top: 14 }}
          >
            <IconTrashBinFilled />
          </IconButton>
        </Box>
      ) : (
        <DropZone
          maxFiles={1}
          accept={{
            'image/jpeg': ['.jpeg', '.jpg'],
            'image/png': ['.png'],
          }}
          maxSize={200 * Math.pow(1024, 2)}
          onDrop={([file]) => {
            handleFileDrop(file!)
            isError.setFalse()
          }}
          onReject={([fileRejections]) => {
            const errorCodes = fileRejections?.errors.map(error => error.code)

            if (errorCodes) isError.setTrue()

            toast.error(
              <Box display="flex" flexDirection="column">
                <Typography
                  variant="caption2"
                  color="text.primary"
                  fontWeight={600}
                >
                  {t(`components.dropzone.file_cant_be_uploaded`)}
                </Typography>

                <Stack>
                  {errorCodes?.map(error => (
                    <Typography
                      variant="caption3"
                      color="text.primary"
                      mt={0.5}
                      key={error}
                    >
                      {errorCodes.length > 1 ? `• ` : ''}
                      {t(`dropzone_errors.${error}`)}
                    </Typography>
                  ))}
                </Stack>
              </Box>,
            )
          }}
          isError={isError.isTrue}
        >
          <Typography
            variant="caption3"
            color={isError.isTrue ? 'error.main' : 'text.secondary'}
            mt={1}
            textAlign="center"
          >
            {t('components.dropzone.supported_format', {
              format: SUPPORTED_FILE_FORMAT,
            })}
            <br />
            {t('components.dropzone.file_max_size', { fileSize: '5mb' })}
          </Typography>
        </DropZone>
      )}
    </DialogContent>
  )
}
